// ABOUTME: Modal for configuring report sharing settings and generating share links.
// ABOUTME: Three-level visibility (private/workspace/public) with tier-gated sub-options.

import { useState, useEffect } from 'react';
import { reportApi } from '@/services/api/client';
import { billingApi } from '@/services/api/billingClient';
import { Button } from '@/components/ui/Button';
import { Lock } from 'lucide-react';
import type { Report, SharingConfig, ReportAnalyticsResponse, PlanFeaturesResponse } from '@/types/api';

type VisibilityLevel = 'private' | 'workspace' | 'public';

interface ShareReportModalProps {
  report: Report;
  isOpen: boolean;
  onClose: () => void;
  onSharingUpdated: (report: Report) => void;
}

export function ShareReportModal({
  report,
  isOpen,
  onClose,
  onSharingUpdated,
}: ShareReportModalProps) {
  const currentConfig = report.sharing_config;
  const currentVisibility = currentConfig?.visibility ?? 'private';

  function toVisibilityLevel(v: SharingConfig['visibility']): VisibilityLevel {
    if (v === 'private') return 'private';
    if (v === 'workspace') return 'workspace';
    return 'public';
  }

  const [visibilityLevel, setVisibilityLevel] = useState<VisibilityLevel>(
    toVisibilityLevel(currentVisibility)
  );
  const [requirePassword, setRequirePassword] = useState(
    currentVisibility === 'password'
  );
  const [password, setPassword] = useState('');
  const [requireEmail, setRequireEmail] = useState(
    currentVisibility === 'email_list'
  );
  const [allowedEmails, setAllowedEmails] = useState<string[]>(
    currentConfig?.allowed_emails ?? []
  );
  const [allowComments, setAllowComments] = useState(
    currentConfig?.allow_comments ?? true
  );
  const [showBranding, setShowBranding] = useState(
    currentConfig?.show_branding ?? true
  );
  const [isSaving, setIsSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [analytics, setAnalytics] = useState<ReportAnalyticsResponse | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [features, setFeatures] = useState<PlanFeaturesResponse | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);

  const [localShareId, setLocalShareId] = useState<string | null>(
    currentConfig?.share_id ?? null
  );

  useEffect(() => {
    if (isOpen) {
      const config = report.sharing_config;
      const vis = config?.visibility ?? 'private';
      setVisibilityLevel(toVisibilityLevel(vis));
      setRequirePassword(vis === 'password');
      setRequireEmail(vis === 'email_list');
      setAllowedEmails(config?.allowed_emails ?? []);
      setAllowComments(config?.allow_comments ?? true);
      setShowBranding(config?.show_branding ?? true);
      setLocalShareId(config?.share_id ?? null);
      setPassword('');
      setHasUnsavedChanges(false);
      setLinkCopied(false);

      // Fetch plan features for the report's workspace
      billingApi.getUsage(report.workspace_id).then((usage) => {
        setFeatures(usage.features ?? null);
        setSubscriptionTier(usage.subscription_tier ?? null);
      }).catch(() => {});
    }
  }, [isOpen, report.id, report.sharing_config?.visibility, report.sharing_config?.share_id]);

  useEffect(() => {
    if (isOpen && localShareId) {
      reportApi.getAnalytics(report.id).then(setAnalytics).catch(() => {});
    }
  }, [isOpen, report.id, localShareId]);

  const shareUrl = localShareId
    ? `${window.location.origin}/r/${localShareId}`
    : null;

  const markChanged = () => setHasUnsavedChanges(true);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // "Only Me" = revoke sharing (unpublish), not publish with 'private'
      if (visibilityLevel === 'private') {
        await reportApi.revokeSharing(report.id);
        setLocalShareId(null);
        setHasUnsavedChanges(false);
        const revokedReport: Report = {
          ...report,
          sharing_config: {
            ...(report.sharing_config ?? {
              iframe_enabled: true,
              allowed_emails: [],
              sso_required: false,
              allow_comments: true,
              show_branding: true,
            }),
            visibility: 'private',
            share_id: null,
          } as SharingConfig,
        };
        onSharingUpdated(revokedReport);
        return;
      }

      let visibility: SharingConfig['visibility'] = 'workspace';
      if (visibilityLevel === 'public') {
        if (requirePassword) {
          visibility = 'password';
        } else if (requireEmail) {
          visibility = 'email_list';
        } else {
          visibility = 'public';
        }
      }

      const updatedReport = await reportApi.updateSharing(report.id, {
        visibility,
        password: password || undefined,
        allowed_emails: allowedEmails.length > 0 ? allowedEmails : undefined,
        allow_comments: allowComments,
        show_branding: showBranding,
      });

      setLocalShareId(updatedReport.sharing_config?.share_id ?? null);
      setHasUnsavedChanges(false);
      setPassword('');
      onSharingUpdated(updatedReport);

      if (updatedReport.sharing_config?.share_id) {
        reportApi.getAnalytics(report.id).then(setAnalytics).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to update sharing:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!confirm('Revoke sharing? The public link will stop working.')) return;
    try {
      await reportApi.revokeSharing(report.id);
      setLocalShareId(null);
      setVisibilityLevel('private');
      setHasUnsavedChanges(false);
      const revokedReport: Report = {
        ...report,
        sharing_config: {
          ...(report.sharing_config ?? {
            iframe_enabled: true,
            allowed_emails: [],
            sso_required: false,
            allow_comments: true,
            show_branding: true,
          }),
          visibility: 'private',
          share_id: null,
        } as SharingConfig,
      };
      onSharingUpdated(revokedReport);
    } catch (err) {
      console.error('Failed to revoke sharing:', err);
    }
  };

  if (!isOpen) return null;

  // Feature gates based on plan tier
  const canPasswordProtect = features?.allow_password_sharing ?? false;
  const canRequireEmail = features?.allow_email_sharing ?? false;
  const canAllowComments = features?.allow_comments ?? false;
  const brandingRequired = features?.require_branding ?? true;
  const isFreeTier = subscriptionTier === 'freemium';
  const featureHint = isFreeTier ? 'Available on paid plans' : 'Not available on your current plan';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-0 rounded-sm shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-surface-900 mb-6">Share Report</h2>

          <div className="space-y-5">
            <fieldset>
              <legend className="text-sm font-medium text-surface-900 mb-3">
                Who can access this report?
              </legend>
              <div className="space-y-2">
                <RadioOption
                  value="private"
                  label="Only me"
                  description="This report is private"
                  selected={visibilityLevel}
                  onChange={(v) => { setVisibilityLevel(v as VisibilityLevel); markChanged(); }}
                />
                <RadioOption
                  value="workspace"
                  label="Workspace members"
                  description="All members of your workspace can view"
                  selected={visibilityLevel}
                  onChange={(v) => { setVisibilityLevel(v as VisibilityLevel); markChanged(); }}
                />
                <RadioOption
                  value="public"
                  label="Anyone with the link"
                  description="Generate a shareable link"
                  selected={visibilityLevel}
                  onChange={(v) => { setVisibilityLevel(v as VisibilityLevel); markChanged(); }}
                />
              </div>
            </fieldset>

            {visibilityLevel === 'public' && (
              <>
                {shareUrl && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 bg-surface-50 border border-surface-200 rounded-sm text-sm text-surface-700"
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCopyLink}
                    >
                      {linkCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                )}

                {analytics && shareUrl && (
                  <div className="text-sm text-surface-500">
                    {analytics.total_views} views &middot; {analytics.unique_viewers} unique viewers
                  </div>
                )}

                <ToggleRow
                  label="Password protection"
                  description={canPasswordProtect ? 'Require a password to view' : undefined}
                  checked={canPasswordProtect ? requirePassword : false}
                  onChange={(v) => { setRequirePassword(v); markChanged(); }}
                  disabled={!canPasswordProtect}
                  upgradeHint={!canPasswordProtect ? featureHint : undefined}
                />
                {requirePassword && canPasswordProtect && (
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); markChanged(); }}
                    placeholder="Set password (leave empty to keep current)"
                    className="w-full px-3 py-2 border border-surface-200 rounded-sm text-sm"
                  />
                )}

                <ToggleRow
                  label="Require email"
                  description={canRequireEmail ? 'Viewers must enter their email before viewing' : undefined}
                  checked={canRequireEmail ? requireEmail : false}
                  onChange={(v) => { setRequireEmail(v); markChanged(); }}
                  disabled={!canRequireEmail}
                  upgradeHint={!canRequireEmail ? featureHint : undefined}
                />
                {requireEmail && canRequireEmail && (
                  <div>
                    <textarea
                      value={allowedEmails.join('\n')}
                      onChange={(e) => {
                        setAllowedEmails(
                          e.target.value.split('\n').filter((v) => v.trim())
                        );
                        markChanged();
                      }}
                      placeholder="Allowed emails (one per line, leave empty for any email)"
                      rows={3}
                      className="w-full px-3 py-2 border border-surface-200 rounded-sm text-sm resize-none"
                    />
                    <p className="text-xs text-surface-500 mt-1">
                      Leave empty to allow any email address
                    </p>
                  </div>
                )}

                <ToggleRow
                  label="Allow comments"
                  description={canAllowComments ? 'Viewers can leave comments on the report' : undefined}
                  checked={canAllowComments ? allowComments : false}
                  onChange={(v) => { setAllowComments(v); markChanged(); }}
                  disabled={!canAllowComments}
                  upgradeHint={!canAllowComments ? featureHint : undefined}
                />

                <ToggleRow
                  label="Show Oleum BI branding"
                  description={
                    brandingRequired
                      ? undefined
                      : 'Show "Powered by Oleum BI" footer on shared reports'
                  }
                  checked={brandingRequired ? true : showBranding}
                  onChange={(v) => { setShowBranding(v); markChanged(); }}
                  disabled={brandingRequired}
                  upgradeHint={brandingRequired ? (isFreeTier ? 'Upgrade to a paid plan to remove branding' : 'Branding required on your current plan') : undefined}
                />
              </>
            )}
          </div>

          <div className="flex items-center justify-between mt-8 pt-4 border-t border-surface-200">
            {localShareId && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleRevoke}
                className="!bg-transparent !shadow-none text-error hover:text-error"
              >
                Revoke access
              </Button>
            )}
            <div className="flex items-center gap-3 ml-auto">
              <Button
                variant="ghost"
                onClick={onClose}
              >
                Cancel
              </Button>
              {hasUnsavedChanges ? (
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isSaving}
                >
                  Save
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={onClose}
                >
                  Done
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RadioOption({
  value,
  label,
  description,
  selected,
  onChange,
}: {
  value: string;
  label: string;
  description: string;
  selected: string;
  onChange: (value: string) => void;
}) {
  const isSelected = selected === value;
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors
        ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-surface-200 hover:bg-surface-50'}`}
    >
      <input
        type="radio"
        name="visibility-level"
        value={value}
        checked={isSelected}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <div
        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0
          ${isSelected ? 'border-primary-500' : 'border-surface-300'}`}
      >
        {isSelected && <div className="w-2 h-2 rounded-full bg-primary-500" />}
      </div>
      <div>
        <p className="text-sm font-medium text-surface-900">{label}</p>
        <p className="text-xs text-surface-500">{description}</p>
      </div>
    </label>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
  upgradeHint,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  upgradeHint?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className={`text-sm font-medium ${disabled ? 'text-surface-400' : 'text-surface-900'}`}>{label}</p>
          {disabled && <Lock className="w-3.5 h-3.5 text-surface-400" />}
        </div>
        {description && (
          <p className="text-xs text-surface-500">{description}</p>
        )}
        {upgradeHint && (
          <p className="text-xs text-primary-500">{upgradeHint}</p>
        )}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0
                    transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    ${checked ? 'bg-primary-500' : 'bg-surface-200'}`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-surface-0 transition-transform
                      ${checked ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </button>
    </div>
  );
}
